
import { Link } from 'react-router-dom';

const steps = [
  {
    icon: 'ri-mail-check-line',
    title: 'Check Your Inbox',
    description: 'We\'ve sent a confirmation email with your quote request details. Check your spam folder if you don\'t see it within a few minutes.',
  },
  {
    icon: 'ri-phone-line',
    title: 'Expect Our Call',
    description: 'A transport specialist will reach out within 30 minutes during business hours to discuss your quote and answer any questions.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Compare & Choose',
    description: 'We\'ll provide you with competitive quotes from our network of verified carriers so you can pick the best option for your needs.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Schedule Pickup',
    description: 'Once you\'ve chosen a carrier, we\'ll coordinate the pickup date and keep you updated every step of the way.',
  },
];

const faqs = [
  {
    question: 'How long does auto transport usually take?',
    answer: 'Transit times vary by distance. Local routes (under 500 miles) typically take 1–3 days, while cross-country shipments take 5–10 days.',
  },
  {
    question: 'Is my vehicle insured during transport?',
    answer: 'Yes. Every carrier in our network carries full insurance coverage. We\'ll provide you with proof of insurance before pickup.',
  },
  {
    question: 'Can I ship personal items in my vehicle?',
    answer: 'Most carriers allow up to 100 lbs of personal items in the trunk. However, items are not covered by carrier insurance.',
  },
];

export default function ThankYouPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/aabcc23d397a531ae9f0b17792c65834/868837240d340ef158720fe61bab18b5.png"
              alt="Auto Transport Logo"
              className="h-12 w-auto"
            />
          </Link>
          <div className="flex items-center gap-6">
            <a
              href="tel:3058719862"
              className="text-base font-medium text-gray-700 hover:text-orange-600 transition-colors cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              (305) 871-9862
            </a>
            <a
              href="mailto:marcos.disla@shiplux.com"
              className="text-base font-medium text-gray-700 hover:text-orange-600 transition-colors cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              marcos.disla@shiplux.com
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Confirmation */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/63642ba9eb5123b0c50467d0646c065e.jpg"
            alt="Auto Transport Success"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center w-full">
          <div className="w-20 h-20 flex items-center justify-center bg-green-500 rounded-full mx-auto mb-8 animate-bounce">
            <i className="ri-check-line text-white text-4xl"></i>
          </div>
          <h1 className="text-5xl font-bold text-white mb-5 leading-tight">
            Your Quote Request Is <span className="text-orange-400">On Its Way!</span>
          </h1>
          <p className="text-xl text-gray-200 leading-relaxed max-w-2xl mx-auto">
            Thank you for choosing us. A dedicated transport specialist will review your details and get back to you shortly with the best rates available.
          </p>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            What Happens Next
          </h2>
          <p className="text-gray-600 text-center mb-14 text-lg max-w-2xl mx-auto">
            Here\'s a quick overview of the process from quote to delivery
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center group">
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] border-t-2 border-dashed border-orange-200"></div>
                )}
                <div className="w-20 h-20 flex items-center justify-center bg-orange-50 rounded-2xl mx-auto mb-5 group-hover:bg-orange-100 transition-colors relative z-10">
                  <i className={`${step.icon} text-orange-600 text-3xl`}></i>
                </div>
                <span className="inline-block bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                  Step {index + 1}
                </span>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-10 flex flex-col md:flex-row items-center gap-10">
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Can\'t Wait? Talk to Us Now
              </h3>
              <p className="text-gray-600 mb-6">
                If you need an immediate quote or have urgent questions, our team is standing by to help you right away.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:3058719862"
                  className="inline-flex items-center bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call (305) 871-9862
                </a>
                <a
                  href="mailto:marcos.disla@shiplux.com"
                  className="inline-flex items-center bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line mr-2"></i>
                  Email Us
                </a>
              </div>
            </div>
            <div className="w-48 h-48 flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Friendly%20professional%20customer%20service%20representative%20wearing%20headset%20smiling%20warmly%2C%20clean%20white%20background%2C%20professional%20portrait%20style%2C%20warm%20lighting%2C%20approachable%20and%20trustworthy%20appearance%2C%20high%20quality%20commercial%20photography&width=400&height=400&seq=thankyou-agent-001&orientation=squarish"
                alt="Customer Support"
                className="w-full h-full object-cover object-top rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-center mb-12 text-lg">
            Quick answers while you wait for your personalized quote
          </p>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-gray-50 rounded-xl border border-gray-100 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
                  <span className="font-semibold text-gray-900 text-base pr-4">{faq.question}</span>
                  <i className="ri-add-line text-xl text-gray-500 group-open:hidden flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <i className="ri-subtract-line text-xl text-orange-600 hidden group-open:block flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                </summary>
                <div className="px-6 pb-5 text-gray-600 text-sm leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-16 bg-orange-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Need to Ship Another Vehicle?
          </h3>
          <p className="text-gray-600 mb-8">
            Get another free quote in just 60 seconds
          </p>
          <Link
            to="/"
            className="inline-flex items-center bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Get Another Quote
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Auto Transport. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
              Privacy Policy
            </Link>
            <span className="text-gray-600">|</span>
            <Link to="/terms-of-service" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">
              Terms of Service
            </Link>
          </div>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 text-xs hover:text-gray-300 transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
